import { useEffect, useState } from "react";
import QRCode from "qrcode";
import { api, errorMessage, unwrap, type AccessLink, type EventData } from "../../api/client";
import { formatDateTime } from "../../domain/format";
import { fr } from "../../i18n/fr";
import { CopyButton, groups, Logo, Notice } from "../../ui/components";

/** Access link, QR code and printable sheet for the crews (SW-WS-50, SW-WS-51). */
export function SharePanel({ eventId, event: e }: { eventId: string; event: EventData }) {
  const [link, setLink] = useState<AccessLink | null>(null);
  const [qr, setQr] = useState<string | null>(null);
  /** second QR code of the printed sheet: download of the application (SW-EV-31) */
  const [appQr, setAppQr] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.GET("/events/{id}/access-link", { params: { path: { id: eventId } } }).then((r) => setLink(unwrap(r))).catch((x) => setError(errorMessage(x, fr.errors)));
  }, [eventId]);
  useEffect(() => {
    if (!link) return;
    QRCode.toDataURL(link.url, { errorCorrectionLevel: "M", margin: 1, width: 480 }).then(setQr).catch(() => setQr(null));
    QRCode.toDataURL(link.appUrl, { errorCorrectionLevel: "M", margin: 1, width: 320 }).then(setAppQr).catch(() => setAppQr(null));
  }, [link]);

  if (error) return <div className="error">{error}</div>;
  if (!link) return <p className="muted">{fr.common.loading}</p>;

  return (
    <section className="stack share">
      <h3>{fr.share.title}</h3>
      <p className="muted small">{fr.share.intro}</p>
      {e.status !== "PUBLISHED" && <Notice kind="warn">{fr.share.notPublished}</Notice>}
      <div className="qr-wrap">{qr && <img src={qr} alt="QR code" className="qr" />}</div>
      <div className="field"><span>{fr.share.link}</span>
        <div className="row"><input readOnly value={link.url} onFocus={(ev) => ev.target.select()} /><CopyButton text={link.url} label={fr.share.copyLink} /></div>
      </div>
      <div className="grid2 access">
        <div className="field"><span>{fr.share.code}</span><code className="key">{groups(link.code)}</code></div>
        <div className="field"><span>{fr.share.key}</span><code className="key">{groups(link.key)}</code></div>
      </div>
      <p className="muted small">{fr.share.manual}</p>
      {link.packs.length > 0 && <div className="small">{fr.share.packs} : {link.packs.map((p) => `${p.name} (${p.version})`).join(", ")}</div>}
      <div className="small">{fr.share.appLink} : <a href={link.appUrl}>{link.appUrl}</a></div>
      <div className="row"><button className="primary" onClick={() => window.print()}>{fr.share.print}</button></div>

      {/* printed sheet (print CSS shows only this block) */}
      <div className="print-sheet">
        <div className="print-brand"><Logo size={56} /><h1>{fr.share.sheetTitle}</h1></div>
        <h2>{e.name}</h2>
        <p>{formatDateTime(e.startAt)} → {formatDateTime(e.endAt)}</p>
        {qr && <img src={qr} alt="QR code" />}
        <ol>{fr.share.sheetSteps.map((st) => <li key={st}>{st}</li>)}</ol>
        <p className="print-access">{fr.share.code} : <strong>{groups(link.code)}</strong> · {fr.share.key} : <strong>{groups(link.key)}</strong></p>
        <p className="url">{link.url}</p>
        <div className="print-app">
          {appQr && <img src={appQr} alt="QR code" className="qr-small" />}
          <p>{fr.share.appLink} : {link.appUrl}</p>
        </div>
        {link.packs.length > 0 && <p>{fr.share.packs} : {link.packs.map((p) => p.name).join(", ")}</p>}
      </div>
    </section>
  );
}
